import {
  BalloonEditor,
  ClassicEditor,
  CustomEditor,
  EditorConfig
} from 'ckeditor5-extra-build'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
// eslint-disable-next-line no-restricted-imports
import {CKEditor} from '@ckeditor/ckeditor5-react'
// eslint-disable-next-line no-restricted-imports
import {useRightPanelContentStore} from '@components/right-panel/right-panel-content/right-panel-content-store'
import {isSafari} from '@extensions/platform'
import {ChatInputKeydownData} from '../../../../api/model/chat'
import {customConfig} from './config'

const forceBlur = (): void => {
  const el = document.createElement('input')
  el.className = 'block w-0 h-0'
  document.body.appendChild(el)
  el.focus()
  el.setSelectionRange(0, 0)
  el.blur()
  el.parentElement?.removeChild(el)
}

type Props = {
  type?: 'classic' | 'balloon'
  value?: string
  onChange?: (data: string) => void
  readonly?: boolean
  config?: EditorConfig
  headerHeight?: number
  autofocus?: boolean
  kindOf:
    | 'task-description'
    | 'chat-input'
    | 'task-history'
    | 'task-template'
    | 'news-description'
  onKeyDown?: (data: ChatInputKeydownData) => void
}

export const CustomCKEditor = forwardRef<CustomEditor, Props>(
  (props: Props, ref) => {
    const {
      value,
      onChange,
      readonly,
      type = 'classic',
      autofocus = true,
      kindOf,
      onKeyDown
    } = props
    const onKeyDownRef = useRef(onKeyDown)
    onKeyDownRef.current = onKeyDown
    const {config, headerHeight} = props

    const editorRef = useRef<CustomEditor>()

    const [panelState] = useRightPanelContentStore()

    const [editor, editorConfig] = useMemo(() => {
      const editor = type === 'balloon' ? BalloonEditor : ClassicEditor
      return [
        editor,
        {...customConfig, ...config, language: Lang.getLang()} as EditorConfig
      ]
    }, [type, config])

    const [id, setId] = useState(0)
    useLayoutEffect(() => {
      setId(id => id + 1)
    }, [type, config])

    // case for cancel button
    useEffect(() => {
      editorRef.current?.setData(value)

      if (!readonly && autofocus) {
        setTimeout(() => {
          editorRef.current?.editing.view.focus()
        }, 100)
      }
    }, [readonly, autofocus, editorRef])

    useEffect(() => {
      updateHeight(editorRef.current, headerHeight)
    }, [readonly, editorRef.current, headerHeight])

    const onReady = useCallback(
      (editor: CustomEditor) => {
        if (!editor) {
          logger.error('onReady: editor is not defined')
          return
        }

        // In Safari `contenteditable` sometimes doesn't actually blur,
        // so we refocus to a temp element to blur properly
        if (isSafari()) {
          editor.editing.view.document.on('blur', forceBlur)
        }

        // See https://answers.microsoft.com/en-us/outlook_com/forum/all/drag-and-drop-works-however-deletes-email-can-we/865f60c3-7980-4465-8a19-4d60b0ae5aba
        editor.editing.view.document.on(
          'dragover',
          (_: any, data: any) => {
            if (data?.dataTransfer) {
              data.dataTransfer.dropEffect = 'copy'
              data.dataTransfer.effectAllowed = 'copy'
            }
          },
          {priority: 'highest'}
        )

        // editor.keystrokes.set(
        //   'backspace',
        //   e => {
        //     e.stopPropagation()
        //   },
        //   {priority: 'highest'}
        // )

        editor.keystrokes.set(
          'enter',
          (e: KeyboardEvent) => {
            e.stopPropagation()
          },
          {priority: 'highest'}
        )

        updateHeight(editor, headerHeight)

        if (kindOf === 'task-description') {
          setTimeout(() => {
            const scroll = panelState?.descriptionScroll
            if (scroll >= 0) {
              const el = document.querySelector('.ck-editor .ck-content')
              if (el) {
                el.scrollTop = scroll
              }
            }
          }, 10)
        }

        if (editor !== editorRef.current) {
          editorRef.current = editor

          if (typeof ref === 'function') {
            ref(editor)
          } else if (ref) {
            ref.current = editor
          }
        }

        editor.editing.view.document.on(
          'keydown',
          (_: unknown, data: ChatInputKeydownData) => {
            onKeyDownRef.current?.(data)
          }
        )
      },
      [ref, editorRef, headerHeight, panelState?.descriptionScroll]
    )

    return (
      <CKEditor
        required
        id={`ckeditor-${id}`}
        editor={editor}
        config={editorConfig}
        data={value}
        disabled={readonly}
        onChange={(_, editor) => {
          const data = editor.getData()
          onChange?.(data || '')
        }}
        onReady={onReady}
      />
    )
  }
)

const updateHeight = (editor: CustomEditor, headerHeight: number) => {
  if (!headerHeight || !editor) {
    return
  }
  editor.editing.view.change((writer: any) => {
    const value = `calc(100vh - ${headerHeight}px)`
    const root = editor.editing.view.document.getRoot()
    writer.setStyle('max-height', value, root)
    writer.setStyle('min-height', value, root)
  })
}
